// frontend/src/components/header/AdminNav.tsx
// Menu du back-office : lien vers l'accueil en cartes, section courante,
// et accès rapide à toutes les sections (liste partagée avec /admin).
import { ChevronDown, LayoutGrid } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '@chainlit/react-client';

import {
  ADMIN_GROUPS,
  ADMIN_SECTIONS,
  TENANT_SECTIONS,
  staffAccess
} from '@/components/admin/sections';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';

import { useAppContext } from '@/hooks/useAppContext';

const AdminNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isMaster } = useAppContext();
  const { user } = useAuth();
  const access = staffAccess(user?.metadata);

  if (
    !location.pathname.startsWith('/admin') &&
    !location.pathname.startsWith('/users')
  ) {
    return null;
  }

  if (!isMaster) {
    return (
      <nav className="flex items-center gap-1 border-r pr-3 mr-3">
        {TENANT_SECTIONS.map(({ path, title, icon: Icon }) => (
          <Link
            key={path}
            to={path}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-primary text-primary-foreground"
          >
            <Icon className="h-4 w-4" aria-hidden="true" />
            {title}
          </Link>
        ))}
      </nav>
    );
  }

  const current = ADMIN_SECTIONS.filter((s) =>
    location.pathname.startsWith(s.path)
  ).sort((a, b) => b.path.length - a.path.length)[0];
  const onHome = location.pathname === '/admin';

  return (
    <nav
      aria-label="Back-office"
      className="flex items-center gap-2 border-r pr-3 mr-3"
    >
      <Link
        to="/admin"
        aria-current={onHome ? 'page' : undefined}
        className={`inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-semibold transition-colors ${
          onHome
            ? 'bg-primary text-primary-foreground'
            : 'text-foreground hover:bg-accent'
        }`}
      >
        <LayoutGrid className="h-4 w-4" aria-hidden="true" />
        <span className="hidden sm:inline">Back-office</span>
      </Link>

      <DropdownMenu>
        <DropdownMenuTrigger className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium border hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          {current ? (
            <>
              <current.icon className="h-4 w-4" aria-hidden="true" />
              <span>{current.title}</span>
            </>
          ) : (
            <span>Sections</span>
          )}
          <ChevronDown
            className="h-3.5 w-3.5 text-muted-foreground"
            aria-hidden="true"
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64">
          {ADMIN_GROUPS.map((group) => ({
            ...group,
            sections: group.sections.filter(access.canSee)
          }))
            .filter((group) => group.sections.length > 0)
            .map((group, i) => (
              <DropdownMenuGroup key={group.title}>
                {i > 0 && <DropdownMenuSeparator />}
                <DropdownMenuLabel className="text-xs text-muted-foreground">
                  {group.title}
                </DropdownMenuLabel>
                {group.sections.map(({ path, title, icon: Icon }) => (
                  <DropdownMenuItem
                    key={path}
                    onSelect={() => navigate(path)}
                    className="gap-2"
                  >
                    <Icon className="h-4 w-4" aria-hidden="true" />
                    {title}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuGroup>
            ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </nav>
  );
};

export default AdminNav;
